import { useState } from "react";

import { useQueryClient } from "react-query";

import {
  GetMessagePreviewStateQuery,
  TriggerMessagePreviewInput,
  useGetMessagePreviewStateQuery,
  useStopWaitingMessagePreviewMutation,
  useTriggerMessagePreviewMutation,
} from "src/graphql";
import { useGovernedRefetchInterval } from "src/hooks/use-governed-refetch-interval";

export type MessagePreviewStateData =
  GetMessagePreviewStateQuery["getMessagePreviewState"];

/**
 * SES inbound mail can't reach a local environment, so dev builds
 * short-circuit the e2e preview flow. Setting VITE_AID_E2E_PREVIEW=true lifts
 * the short-circuit so the full flow can be exercised locally with
 * hand-injected inbound events.
 */
export const isMessagePreviewDevShortCircuited = (): boolean =>
  Boolean(import.meta.env.DEV) &&
  import.meta.env.VITE_AID_E2E_PREVIEW !== "true";

export type MessagePreviewStateMachine = {
  state: MessagePreviewStateData | undefined;
  isStateLoading: boolean;
  stateError: string | undefined;
  triggerPreview: (payload: TriggerMessagePreviewInput) => Promise<void>;
  isTriggering: boolean;
  stopWaiting: () => Promise<void>;
  isStopping: boolean;
};

export const useMessagePreviewStateMachine = ({
  messageId,
  enabled,
}: {
  messageId: string;
  enabled: boolean;
}): MessagePreviewStateMachine => {
  const client = useQueryClient();

  const { refetchInterval, resetBackoff } =
    useGovernedRefetchInterval<GetMessagePreviewStateQuery>({
      baseMs: 2000,
      // A waiting row returns identical data poll-over-poll, so backoff is
      // expected to engage during the wait. Cap it low enough that an
      // accepted render never sits unseen for long.
      maxMs: 10000,
      // Key on lifecycle progress only — requestedAt/error churn shouldn't
      // defeat backoff, and receivedAt changing is exactly the "new render
      // landed" signal that should reset it.
      compareKey: (data) => [
        data?.getMessagePreviewState.status,
        data?.getMessagePreviewState.receivedAt,
      ],
    });

  const query = useGetMessagePreviewStateQuery(
    { messageId },
    {
      enabled,
      refetchInterval: (data, query) =>
        data?.getMessagePreviewState.status === "waiting"
          ? refetchInterval(data, query)
          : false,
    },
  );

  // Overriding onSuccess intentionally disables the global
  // invalidate-active-queries behavior: previews fire on a debounce while the
  // marketer edits, and refetching every mounted query on each send is
  // wasteful. The preview-state cache is maintained explicitly below.
  const triggerMutation = useTriggerMessagePreviewMutation({
    onSuccess: () => {},
  });
  const stopWaitingMutation = useStopWaitingMessagePreviewMutation({
    onSuccess: () => {},
  });

  const [triggersInFlight, setTriggersInFlight] = useState(0);

  const triggerPreview = async (payload: TriggerMessagePreviewInput) => {
    setTriggersInFlight((count) => count + 1);
    try {
      await triggerMutation.mutateAsync({ messageId, payload });
      resetBackoff();
      // react-query only recomputes refetchInterval when a fetch settles, so
      // after the row moves back to waiting one explicit fetch is needed to
      // re-arm the poll timer (it stopped on displaying/failed/none).
      await query.refetch();
    } finally {
      setTriggersInFlight((count) => count - 1);
    }
  };

  const stopWaiting = async () => {
    const result = await stopWaitingMutation.mutateAsync({ messageId });
    client.setQueryData(useGetMessagePreviewStateQuery.getKey({ messageId }), {
      getMessagePreviewState: result.stopWaitingMessagePreview,
    });
  };

  return {
    state: query.data?.getMessagePreviewState,
    isStateLoading: query.isLoading,
    stateError: query.isError
      ? ((query.error as Error | undefined)?.message ??
        "Failed to load the preview state")
      : undefined,
    triggerPreview,
    isTriggering: triggersInFlight > 0,
    stopWaiting,
    isStopping: stopWaitingMutation.isLoading,
  };
};
